/**
 * 文件路径: D:\XXY\MeiShan_Project\frontend\shared\stores\user.ts
 * 用户状态管理
 */

import { defineStore } from 'pinia'
import 'pinia-plugin-persistedstate'
import { ref, computed } from 'vue'
import { AuthService, TokenManager } from '../api'
import { clearUserCache } from '../utils/route-guards'
import type { User, LoginRequest, LoginResponse } from '../types'

export const useUserStore = defineStore('user', () => {
  // 状态
  const currentUser = ref<User | null>(null)
  const isAuthenticated = ref(false)
  const isLoading = ref(false)
  
  // 计算属性
  const userRole = computed<string | null>(() => {
    if (!currentUser.value) return null
    
    if (currentUser.value.is_superuser) return 'superuser'
    if (currentUser.value.operator_department_id) return 'operator'
    if (currentUser.value.merchant_id) return 'merchant'
    if (currentUser.value.enterprise_id) return 'enterprise'
    
    // 兼容后端直接返回的 user_type 字段
    if (currentUser.value.user_type && ['operator', 'merchant', 'enterprise'].includes(currentUser.value.user_type)) {
      return currentUser.value.user_type
    }
    
    return 'unknown'
  })
  
  const hasPermission = computed<(permission: string) => boolean>(() => (permission: string): boolean => {
    if (!currentUser.value) return false
    // ⭐ 超级管理员短路放行：后端对 superuser 返回空权限数组，前端统一绕过
    if (currentUser.value.is_superuser) return true
    if (!currentUser.value.permissions) return false
    return currentUser.value.permissions.includes(permission)
  })
  
  const hasAnyPermission = computed<(permissions: string[]) => boolean>(() => (permissions: string[]): boolean => {
    if (!currentUser.value) return false
    if (currentUser.value.is_superuser) return true
    if (!currentUser.value.permissions) return false
    return permissions.some(permission => currentUser.value!.permissions.includes(permission))
  })
  
  const hasAllPermissions = computed<(permissions: string[]) => boolean>(() => (permissions: string[]): boolean => {
    if (!currentUser.value) return false
    if (currentUser.value.is_superuser) return true
    if (!currentUser.value.permissions) return false
    return permissions.every(permission => currentUser.value!.permissions.includes(permission))
  })
  
  // 操作方法
  const setUser = (user: User | null) => {
    currentUser.value = user
    isAuthenticated.value = !!user
  }
  
  const login = async (credentials: LoginRequest): Promise<LoginResponse> => {
    console.log('[user.ts] login action started.');
    isLoading.value = true;
    try {
      console.log('[user.ts] Calling AuthService.login with credentials:', credentials);
      const response = await AuthService.login(credentials);
      console.log('[user.ts] AuthService.login returned:', response);
      
      // 仅保存令牌到本地存储，移除直接修改axios实例头的操作
      console.log('[user.ts] Setting tokens...');
      TokenManager.setTokens(response.access, response.refresh);
      console.log('[user.ts] Tokens set in localStorage.');
      
      // 🔧 关键修复：登录接口返回的 user 对象不包含 permissions 和 roles
      // 必须调用 /users/me/ 重新获取完整的用户信息
      console.log('[user.ts] Fetching complete user data from /users/me/...');
      const completeUser = await fetchCurrentUser(true);  // ⭐ 强制刷新
      console.log('[user.ts] Complete user data fetched:', {
        username: completeUser?.username,
        permissions: completeUser?.permissions?.length,
        roles: completeUser?.roles
      });
      
      return response;
    } catch (error) {
      // 捕获API调用时的任何错误
      console.error("[user.ts] Error during login process in user store:", error);
      // 将原始错误向上层调用者（useAuth.ts）抛出，以便在那里统一处理UI反馈
      throw error;
    } finally {
      isLoading.value = false;
      console.log('[user.ts] login action finished.');
    }
  }
  
  const logout = async () => {
    isLoading.value = true
    try {
      const refreshToken = TokenManager.getRefreshToken()
      if (refreshToken) {
        await AuthService.logout(refreshToken)
      }
    } catch (error) {
      console.warn('Logout API call failed:', error)
    } finally {
      // 无论API调用是否成功，都要清理本地状态
      TokenManager.clearTokens()
      setUser(null)
      // ⭐ 清除路由守卫的用户缓存
      clearUserCache()
      isLoading.value = false
    }
  }
  
  /**
   * 获取当前用户信息
   * @param force - 是否强制刷新（默认 false）
   * 
   * ⭐ 缓存策略（2025-12-28 P0 修复）：
   * - 如果 currentUser 已有值且不强制刷新，直接返回缓存
   * - 避免 route-guards / BaseLayout / checkAuth 多处重复请求
   */
  const fetchCurrentUser = async (force = false): Promise<User | null> => {
    if (!TokenManager.hasValidAuth()) {
      setUser(null)
      return null
    }
    
    // ⭐ 缓存检查：已有用户信息且不强制刷新，直接返回
    if (!force && currentUser.value) {
      console.debug('[user.ts] Using cached currentUser:', currentUser.value.username)
      return currentUser.value
    }
    
    isLoading.value = true
    try {
      const user = await AuthService.getCurrentUser()
      
      // 🔍 调试日志：检查从 AuthService 获取的数据
      console.log('[user.ts] Fetched user from AuthService:', {
        username: user.username,
        permissions: user.permissions?.length,
        roles: user.roles,
        fullUser: user
      })
      
      setUser(user)
      
      // 🔍 调试日志：检查 setUser 后的状态
      console.log('[user.ts] After setUser, currentUser:', {
        username: currentUser.value?.username,
        permissions: currentUser.value?.permissions?.length,
        roles: currentUser.value?.roles
      })
      
      return user
    } catch (error) {
      console.error('Failed to fetch current user:', error)
      // 如果获取用户信息失败，清除令牌
      await logout() // 使用logout来确保状态完全清理
      return null
    } finally {
      isLoading.value = false
    }
  }
  
  const updateUser = (updates: Partial<User>) => {
    if (currentUser.value) {
      currentUser.value = { ...currentUser.value, ...updates }
    }
  }
  
  const changePassword = async (oldPassword: string, newPassword: string) => {
    isLoading.value = true
    try {
      await AuthService.changePassword(oldPassword, newPassword)
    } finally {
      isLoading.value = false
    }
  }
  
  const resetPassword = async (email: string) => {
    isLoading.value = true
    try {
      await AuthService.resetPassword(email)
    } finally {
      isLoading.value = false
    }
  }
  
  /**
   * 检查认证状态
   * ⭐ P0 修复（2025-12-28）：不主动触发 API 请求
   * - Pinia persist 会自动恢复 currentUser
   * - 路由守卫会在需要时获取用户信息
   * - 避免 store 初始化时的冗余请求
   */
  const checkAuth = (): boolean => {
    // ⭐ 使用 hasValidAuth() 检查 token 有效性（含过期判断）
    // 双 token 均过期时自动清除 localStorage 中的过期 token
    const valid = TokenManager.hasValidAuth()
    if (!valid) {
      setUser(null)
      return false
    }
    
    return true
  }
  
  // 初始化时检查认证状态（不触发 API）
  checkAuth()
  
  return {
    // 状态
    currentUser,
    isAuthenticated,
    isLoading,
    
    // 计算属性
    userRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    
    // 操作方法
    setUser,
    login,
    logout,
    fetchCurrentUser,
    updateUser,
    changePassword,
    resetPassword,
    checkAuth
  }
}, {
  // 持久化配置
  // @ts-expect-error -- pinia-plugin-persistedstate typing differs between consuming package tsconfigs
  persist: {
    // 关键修复：为不同后台添加存储前缀，避免多个后台并存时的状态串扰
    key: `${TokenManager.getKeyPrefix()}user-store`,
    storage: localStorage,
    paths: ['currentUser', 'isAuthenticated']
  }
})
